import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Upload, FileText, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MemberPassportCell = ({ companyId, memberId, token }) => {
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const fetchPassport = async () => {
    try {
      const res = await axios.get(
        `${API}/companies/${companyId}/members/${memberId}/passport`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPassport(res.data?.passport || null);
    } catch (err) {
      // Silent — treat as not uploaded
      setPassport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && memberId && token) fetchPassport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, memberId, token]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large (max 10MB)');
      return;
    }
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API}/companies/${companyId}/members/${memberId}/passport`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setPassport({
        document_id: res.data.document_id,
        file_url: res.data.file_url,
        file_name: res.data.file_name,
        uploaded_at: res.data.uploaded_at,
        version: res.data.version,
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return '';
    }
  };

  if (loading) {
    return <Loader2 className="w-4 h-4 animate-spin text-gray-500" data-testid={`passport-loading-${memberId}`} />;
  }

  return (
    <div className="space-y-1" data-testid={`member-passport-cell-${memberId}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        onChange={handleFile}
        className="hidden"
        data-testid={`passport-file-input-${memberId}`}
      />
      {passport ? (
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#059669] flex-shrink-0" />
          <div className="text-xs">
            <div className="text-white truncate max-w-[180px]" title={passport.file_name}>
              {passport.file_name}
            </div>
            <div className="text-gray-500">
              v{passport.version || 1} · {formatDate(passport.uploaded_at)}
            </div>
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="ml-1 px-2 py-0.5 text-xs text-[#059669] border border-[#059669]/40 rounded hover:bg-[#059669]/10 disabled:opacity-50"
            data-testid={`passport-replace-btn-${memberId}`}
          >
            {uploading ? '…' : 'Replace'}
          </button>
        </div>
      ) : (
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-300 border border-gray-700 rounded hover:bg-gray-800 disabled:opacity-50"
          data-testid={`passport-upload-btn-${memberId}`}
        >
          {uploading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Upload className="w-3.5 h-3.5" />
          )}
          {uploading ? 'Uploading…' : 'Upload Passport'}
        </button>
      )}
      {error && (
        <div className="text-xs text-red-400" data-testid={`passport-error-${memberId}`}>{error}</div>
      )}
    </div>
  );
};

export default MemberPassportCell;
